import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Loader2 } from "lucide-react";
import {
  useSubtasks,
  useCreateSubtask,
  useToggleSubtask,
  useDeleteSubtask,
} from "@/hooks/useProjectPlanning";

export function SubtaskChecklist({
  backlogItemId,
  "aria-labelledby": labelledBy,
}: {
  backlogItemId: string;
  /** id do rótulo "Subtarefas" do diálogo. */
  "aria-labelledby"?: string;
}) {
  const { data: subtasks = [], isLoading } = useSubtasks(backlogItemId);
  const createSubtask = useCreateSubtask(backlogItemId);
  const toggleSubtask = useToggleSubtask(backlogItemId);
  const deleteSubtask = useDeleteSubtask(backlogItemId);
  const [newTitle, setNewTitle] = useState("");

  const handleAdd = () => {
    if (!newTitle.trim()) return;
    createSubtask.mutate(newTitle.trim(), { onSuccess: () => setNewTitle("") });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div role="group" aria-labelledby={labelledBy} className="space-y-2">
      {subtasks.length > 0 && (
        <div className="space-y-1.5">
          {subtasks.map((st) => (
            <div key={st.id} className="group flex items-center gap-2">
              <Checkbox
                checked={st.is_done}
                onCheckedChange={(checked) =>
                  toggleSubtask.mutate({ id: st.id, is_done: !!checked })
                }
              />
              <span
                className={`flex-1 text-sm ${st.is_done ? "text-muted-foreground line-through" : ""}`}
              >
                {st.title}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive opacity-0 group-hover:opacity-100"
                onClick={() => deleteSubtask.mutate(st.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="Adicionar subtarefa..."
          className="h-8 text-sm"
        />
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={handleAdd}
          disabled={createSubtask.isPending}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
